// What? A small, reusable "add section" button used by the document structure UI.
// Why? Sections can be added in more than one place (the header appends to the end,
//      each section card inserts directly below itself), and every one of those
//      call sites should look and behave identically.
// How? Purely presentational: it renders a Joy button with an optional tooltip and
//      busy state, and hands the click back to the caller, which owns the insert logic.

import React from "react";
import { Button, Tooltip } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";

export interface AddSectionButtonProps {
  /** Invoked when the button is pressed. The caller decides where the section lands. */
  onClick: () => void;
  /** Visible label. Defaults to "Add Section". */
  label?: string;
  /** Tooltip text. Omit for no tooltip. */
  tooltip?: string;
  /** Accessible name, when the visible label is not descriptive enough on its own. */
  ariaLabel?: string;
  disabled?: boolean;
  /** Shows a spinner and blocks further clicks while a section is being created. */
  loading?: boolean;
  variant?: "solid" | "outlined" | "soft" | "plain";
  color?: "primary" | "neutral";
  size?: "sm" | "md" | "lg";
}

const AddSectionButton: React.FC<AddSectionButtonProps> = ({
  onClick,
  label = "Add Section",
  tooltip,
  ariaLabel,
  disabled = false,
  loading = false,
  variant = "solid",
  color = "primary",
  size = "sm",
}) => {
  const button = (
    <Button
      variant={variant}
      color={color}
      size={size}
      onClick={onClick}
      disabled={disabled}
      loading={loading}
      startDecorator={<AddIcon />}
      aria-label={ariaLabel || label}
    >
      {label}
    </Button>
  );

  if (!tooltip) return button;

  return (
    <Tooltip title={tooltip} placement="top">
      {/* span keeps the tooltip working while the button is disabled */}
      <span>{button}</span>
    </Tooltip>
  );
};

export default AddSectionButton;
